import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  CircularProgress,
  withStyles,
  Divider,
} from "@material-ui/core";
import { Check } from "@material-ui/icons";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";



const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    disabled={props.disabled}
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

// Product Modality Tab Component
const ProductModalityTab = ({
  drugTypes,
  selectedConfigs,
  onToggle,
  readOnly = false,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress color="primary" />
      </div>
    );
  }

  if (!drugTypes || !Array.isArray(drugTypes) || drugTypes.length === 0) {
    return <Typography>No product modality types available</Typography>;
  }

  return (
    <TableContainer component={Paper} className="config-table">
      <Table>
        <TableHead className="table-head">
          <TableRow>
            <TableCell>Selection</TableCell>
            <TableCell>Product Modality</TableCell>
            <TableCell>Product Modality ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {drugTypes.map((type, idx) => (
            <TableRow key={type.drugTypeID}>
              <TableCell>
                <span style={{ position: "relative" }}>
                  <IOSSwitch
                    className="ios-switch"
                    checked={selectedConfigs[idx] === true}
                    onChange={() => onToggle(idx)}
                    color="primary"
                    disabled={readOnly}
                  />
                  {selectedConfigs[idx] && <Check className="checked-switch" />}
                </span>
              </TableCell>
              <TableCell>{type.drugTypeName}</TableCell>
              <TableCell>{type.drugTypeID}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Manufacturing Supply Type Tab Component
const ManufacturingSupplyTypeTab = ({
  supplyTypes,
  selectedConfigs,
  onToggle,
  readOnly = false,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress color="primary" />
      </div>
    );
  }

  if (!supplyTypes || !Array.isArray(supplyTypes) || supplyTypes.length === 0) {
    return <Typography>No manufacturing supply types available</Typography>;
  }

  return (
    <TableContainer component={Paper} className="config-table">
      <Table>
        <TableHead className="table-head">
          <TableRow>
            <TableCell>Selection</TableCell>
            <TableCell>Manufacturing Supply Type</TableCell>
            <TableCell>Manufacturing Supply Type ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {supplyTypes.map((type, idx) => (
            <TableRow key={type.supplyTypeID}>
              <TableCell>
                <span style={{ position: "relative" }}>
                  <IOSSwitch
                    className="ios-switch"
                    checked={selectedConfigs[idx] === true}
                    onChange={() => onToggle(idx)}
                    color="primary"
                    disabled={readOnly}
                  />
                  {selectedConfigs[idx] && <Check className="checked-switch" />}
                </span>
              </TableCell>
              <TableCell>{type.supplyTypeName}</TableCell>
              <TableCell>{type.supplyTypeID}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Plant Type Tab Component
const PlantTypeTab = ({
  plantTypes,
  selectedConfigs,
  onToggle,
  readOnly = false,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress color="primary" />
      </div>
    );
  }

  if (!plantTypes || !Array.isArray(plantTypes) || plantTypes.length === 0) {
    return <Typography>No plant types available</Typography>;
  }

  return (
    <TableContainer component={Paper} className="config-table">
      <Table>
        <TableHead className="table-head">
          <TableRow>
            <TableCell>Selection</TableCell>
            <TableCell>Plant Type</TableCell>
            <TableCell>Plant Type ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {plantTypes.map((type, idx) => (
            <TableRow key={type.plantTypeID}>
              <TableCell>
                <span style={{ position: "relative" }}>
                  <IOSSwitch
                    className="ios-switch"
                    checked={selectedConfigs[idx] === true}
                    onChange={() => onToggle(idx)}
                    color="primary"
                    disabled={readOnly}
                  />
                  {selectedConfigs[idx] && <Check className="checked-switch" />}
                </span>
              </TableCell>
              <TableCell>{type.plantTypeName}</TableCell>
              <TableCell>{type.plantTypeID}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Manufacturing Facility Type Tab Component
const ManufacturingFacilityTypeTab = ({
  facilityTypes,
  selectedConfigs,
  onToggle,
  readOnly = false,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress color="primary" />
      </div>
    );
  }

  if (!facilityTypes || !Array.isArray(facilityTypes) || facilityTypes.length === 0) {
    return <Typography>No manufacturing facility types available</Typography>;
  }

  return (
    <TableContainer component={Paper} className="config-table">
      <Table>
        <TableHead className="table-head">
          <TableRow>
            <TableCell>Selection</TableCell>
            <TableCell>Manufacturing Facility Type</TableCell>
            <TableCell>Manufacturing Facility Type ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {facilityTypes.map((type, idx) => (
            <TableRow key={type.facilityTypeID}>
              <TableCell>
                <span style={{ position: "relative" }}>
                  <IOSSwitch
                    className="ios-switch"
                    checked={selectedConfigs[idx] === true}
                    onChange={() => onToggle(idx)}
                    color="primary"
                    disabled={readOnly}
                  />
                  {selectedConfigs[idx] && <Check className="checked-switch" />}
                </span>
              </TableCell>
              <TableCell>{type.facilityTypeName}</TableCell>
              <TableCell>{type.facilityTypeID}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const PlantConfigurationSection = React.forwardRef(
  (
    {
      formData,
      readOnly = false,
      onFieldChange,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
    },
    ref
  ) => {

    const [tabIndex, setTabIndex] = useState(0);
    const [selectedConfigs, setSelectedConfigs] = useState({
      productModality: [],
      manufacturingSupplyType: [],
      plantType: [],
      manufacturingFacilityType: [],
    });
    const [tabsLoading, setTabsLoading] = useState(true);
    const [error, setError] = useState("");
    const API_BASE_URL = useApiBaseUrl(appUrl)

    // Log when component mounts
    useEffect(() => {
      return () => {
        console.warn("PlantConfigurationSection unmounted");
      };
    }, []);


    // Expose methods to parent component via ref
    React.useImperativeHandle(ref, () => ({
      getFormData: () => {
        // Return the current configuration data with full objects (ID + Name)
        return {
          drugTypeIDList: getDrugTypeIDListWithNames(),
          supplyTypeIDList: getSupplyTypeIDListWithNames(),
          plantTypeIDList: getPlantTypeIDListWithNames(),
          facilityTypeIDList: getFacilityTypeIDListWithNames(),
        };
      },
      getPayload: () => {
        // Return the payload for API with only IDs in arrays
        return {
          vendorMosymphonyID,
          brandOwnerMosymphonyID,
          updatePlantDetailsFlag: true,
          vendorPlantDetails: {
            drugTypeIDList: getDrugTypeIDList(),
            supplyTypeIDList: getSupplyTypeIDList(),
            plantTypeIDList: getPlantTypeIDList(),
            facilityTypeIDList: getFacilityTypeIDList(),
          },
        };
      },
      validateForm: () => {
        // No validation needed for this component
        return true;
      },
      resetForm: () => {
        // Reset local state and flags so that on readOnly toggle the component refetches and shows view data
        setTabIndex(0);
        setSelectedConfigs({
          productModality: [],
          manufacturingSupplyType: [],
          plantType: [],
          manufacturingFacilityType: [],
        });
        setFacilityTypes([]);
        setSupplyTypes([]);
        setDrugTypes([]);
        setPlantTypes([]);
        dataLoadedRef.current = false;
        apiCallAttemptedRef.current = false;
        setTabsLoading(true);
      },
    }));

    // Helper functions to get the selected IDs (kept for backward compatibility if needed)
    const getDrugTypeIDList = () => {
      const drugTypeIDList = [];
      selectedConfigs.productModality.forEach((selected, i) => {
        if (selected && drugTypes[i]) {
          drugTypeIDList.push(drugTypes[i].drugTypeID);
        }
      });
      return drugTypeIDList;
    };

    const getSupplyTypeIDList = () => {
      const supplyTypeIDList = [];
      selectedConfigs.manufacturingSupplyType.forEach((selected, i) => {
        if (selected && supplyTypes[i]) {
          supplyTypeIDList.push(supplyTypes[i].supplyTypeID);
        }
      });
      return supplyTypeIDList;
    };

    const getPlantTypeIDList = () => {
      const plantTypeIDList = [];
      selectedConfigs.plantType.forEach((selected, i) => {
        if (selected && plantTypes[i]) {
          plantTypeIDList.push(plantTypes[i].plantTypeID);
        }
      });
      return plantTypeIDList;
    };

    const getFacilityTypeIDList = () => {
      const facilityTypeIDList = [];
      selectedConfigs.manufacturingFacilityType.forEach((selected, i) => {
        if (selected && facilityTypes[i]) {
          facilityTypeIDList.push(facilityTypes[i].facilityTypeID);
        }
      });
      return facilityTypeIDList;
    };

    // Helper functions to get the selected items with full objects (ID + Name)
    const getDrugTypeIDListWithNames = () => {
      const drugTypeIDList = [];
      selectedConfigs.productModality.forEach((selected, i) => {
        if (selected && drugTypes[i]) {
          drugTypeIDList.push({
            drugTypeID: drugTypes[i].drugTypeID,
            drugTypeName: drugTypes[i].drugTypeName
          });
        }
      });
      return drugTypeIDList;
    };

    const getSupplyTypeIDListWithNames = () => {
      const supplyTypeIDList = [];
      selectedConfigs.manufacturingSupplyType.forEach((selected, i) => {
        if (selected && supplyTypes[i]) {
          supplyTypeIDList.push({
            supplyTypeID: supplyTypes[i].supplyTypeID,
            supplyTypeName: supplyTypes[i].supplyTypeName
          });
        }
      });
      return supplyTypeIDList;
    };

    const getPlantTypeIDListWithNames = () => {
      const plantTypeIDList = [];
      selectedConfigs.plantType.forEach((selected, i) => {
        if (selected && plantTypes[i]) {
          plantTypeIDList.push({
            plantTypeID: plantTypes[i].plantTypeID,
            plantTypeName: plantTypes[i].plantTypeName
          });
        }
      });
      return plantTypeIDList;
    };

    const getFacilityTypeIDListWithNames = () => {
      const facilityTypeIDList = [];
      selectedConfigs.manufacturingFacilityType.forEach((selected, i) => {
        if (selected && facilityTypes[i]) {
          facilityTypeIDList.push({
            facilityTypeID: facilityTypes[i].facilityTypeID,
            facilityTypeName: facilityTypes[i].facilityTypeName
          });
        }
      });
      return facilityTypeIDList;
    };

    const [facilityTypes, setFacilityTypes] = useState([]);
    const [supplyTypes, setSupplyTypes] = useState([]);
    const [drugTypes, setDrugTypes] = useState([]);
    const [plantTypes, setPlantTypes] = useState([]);

    // Fetch configuration data on component mount
    // We'll use a ref to track the loading state
    const dataLoadedRef = React.useRef(false);
    const apiCallAttemptedRef = React.useRef(false);

    // Reset loading state when component unmounts
    useEffect(() => {
      return () => {
        // We won't use static variables anymore to avoid issues with stale data
        apiCallAttemptedRef.current = false;
      };
    }, []);

    // Reset data loaded flag when transitioning from read-only to edit mode
    useEffect(() => {
      if (!readOnly && dataLoadedRef.current) {
        // When entering edit mode, reset the data loaded flag to force re-processing
        dataLoadedRef.current = false;
        //console.log('PlantConfigurationSection - Reset data loaded flag for edit mode');
      }
    }, [readOnly]);

    // Extract type data from formData if available
    useEffect(() => {
      // Skip if already attempted to load data in this session and successfully loaded
      // But only skip if we're in read-only mode, as edit mode needs to re-process data
      if (apiCallAttemptedRef.current && dataLoadedRef.current && readOnly) {
        setTabsLoading(false);
        return;
      }

      // Always fetch the data from API to get the complete list of options
      const fetchConfigData = async () => {
        setTabsLoading(true);
        setError(""); // Clear any previous errors

        // Mark that we've attempted an API call in this session
        apiCallAttemptedRef.current = true;

        try {
          // Fetch Facility Types
          const facilityTypesRes = await axios.get(
            `${API_BASE_URL}/fetchFacilityTypesDirect/true`
          );
          const allFacilityTypes = facilityTypesRes.data?.data?.facilityTypesList || [];
          
          //console.log('PlantConfigurationSection - Facility Types - readOnly:', readOnly);
          //console.log('PlantConfigurationSection - Facility Types - formData.facilityTypeIDList:', formData?.facilityTypeIDList);
          //console.log('PlantConfigurationSection - Facility Types - allFacilityTypes:', allFacilityTypes);
          
          if (readOnly) {
            // In read-only mode, only show facility types that exist in viewCmoDetails data
            if (formData?.facilityTypeIDList?.length > 0) {
              const filteredFacilityTypes = allFacilityTypes.filter(facilityType => 
                formData.facilityTypeIDList.some(item => {
                  const id = typeof item === 'object' ? item.facilityTypeID : item;
                  return Number(id) === Number(facilityType.facilityTypeID);
                })
              );
              setFacilityTypes(filteredFacilityTypes);
            } else {
              // No items selected, show empty array
              setFacilityTypes([]);
            }
          } else if (!readOnly) {
            // In edit mode (!readOnly), combine viewCmoDetails data with new facility types from API
            //console.log('PlantConfigurationSection - Facility Types - ENTERING EDIT MODE LOGIC');
            const combinedFacilityTypes = [];
            
            // First, add facility types from viewCmoDetails data (with their current state)
            if (formData?.facilityTypeIDList && formData.facilityTypeIDList.length > 0) {
              //console.log('PlantConfigurationSection - Facility Types - Processing viewCmoDetails facility types:', formData.facilityTypeIDList);
              formData.facilityTypeIDList.forEach(item => {
                const facilityTypeId = typeof item === 'object' ? item.facilityTypeID : item;
                //console.log('PlantConfigurationSection - Facility Types - Looking for facilityTypeId:', facilityTypeId);
                const existingFacilityType = allFacilityTypes.find(ft => Number(ft.facilityTypeID) === Number(facilityTypeId));
                //console.log('PlantConfigurationSection - Facility Types - Found existing facility type:', existingFacilityType);
                if (existingFacilityType) {
                  combinedFacilityTypes.push({
                    ...existingFacilityType,
                    activated: true, // Mark as activated since it exists in viewCmoDetails
                    fromViewData: true // Flag to identify it came from viewCmoDetails
                  });
                }
              });
            } 
            
            // Then, add new facility types from API that don't exist in viewCmoDetails
            //console.log('PlantConfigurationSection - Facility Types - Processing new facility types from API');
            allFacilityTypes.forEach(facilityType => {
              const existsInViewData = formData?.facilityTypeIDList?.some(item => {
                const id = typeof item === 'object' ? item.facilityTypeID : item;
                return Number(id) === Number(facilityType.facilityTypeID);
              });
              //console.log(`PlantConfigurationSection - Facility Types - Facility type ${facilityType.facilityTypeID} exists in view data:`, existsInViewData);
              if (!existsInViewData) {
                combinedFacilityTypes.push({
                  ...facilityType,
                  activated: false, // Mark as not activated since it's new
                  fromViewData: false // Flag to identify it's new from API
                });
              }
            });
            
            //console.log('PlantConfigurationSection - Facility Types - combinedFacilityTypes:', combinedFacilityTypes);
            setFacilityTypes(combinedFacilityTypes);
          }

          // Fetch Supply Types
          const supplyTypesRes = await axios.get(
            `${API_BASE_URL}/fetchPlantSupplyTypesDirect/true`
          );
          const allSupplyTypes = supplyTypesRes.data?.data?.supplyTypeList || [];
          
          //console.log('PlantConfigurationSection - Supply Types - readOnly:', readOnly);
          //console.log('PlantConfigurationSection - Supply Types - formData.supplyTypeIDList:', formData?.supplyTypeIDList);
          //console.log('PlantConfigurationSection - Supply Types - allSupplyTypes:', allSupplyTypes);
          
          if (readOnly) {
            // In read-only mode, only show supply types that exist in viewCmoDetails data
            if (formData?.supplyTypeIDList?.length > 0) {
              const filteredSupplyTypes = allSupplyTypes.filter(supplyType => 
                formData.supplyTypeIDList.some(item => {
                  const id = typeof item === 'object' ? item.supplyTypeID : item;
                  return Number(id) === Number(supplyType.supplyTypeID);
                })
              );
              setSupplyTypes(filteredSupplyTypes);
            } else {
              // No items selected, show empty array
              setSupplyTypes([]);
            }
          } else if (!readOnly) {
            // In edit mode (!readOnly), combine viewCmoDetails data with new supply types from API
            //console.log('PlantConfigurationSection - Supply Types - ENTERING EDIT MODE LOGIC');
            const combinedSupplyTypes = [];
            
            // First, add supply types from viewCmoDetails data (with their current state)
            if (formData?.supplyTypeIDList && formData.supplyTypeIDList.length > 0) {
              //console.log('PlantConfigurationSection - Supply Types - Processing viewCmoDetails supply types:', formData.supplyTypeIDList);
              formData.supplyTypeIDList.forEach(item => {
                const supplyTypeId = typeof item === 'object' ? item.supplyTypeID : item;
                //console.log('PlantConfigurationSection - Supply Types - Looking for supplyTypeId:', supplyTypeId);
                const existingSupplyType = allSupplyTypes.find(st => Number(st.supplyTypeID) === Number(supplyTypeId));
                //console.log('PlantConfigurationSection - Supply Types - Found existing supply type:', existingSupplyType);
                if (existingSupplyType) {
                  combinedSupplyTypes.push({
                    ...existingSupplyType,
                    activated: true, // Mark as activated since it exists in viewCmoDetails
                    fromViewData: true // Flag to identify it came from viewCmoDetails
                  });
                }
              });
            }
            
            // Then, add new supply types from API that don't exist in viewCmoDetails
            //console.log('PlantConfigurationSection - Supply Types - Processing new supply types from API');
            allSupplyTypes.forEach(supplyType => {
              const existsInViewData = formData?.supplyTypeIDList?.some(item => {
                const id = typeof item === 'object' ? item.supplyTypeID : item;
                return Number(id) === Number(supplyType.supplyTypeID);
              });
              //console.log(`PlantConfigurationSection - Supply Types - Supply type ${supplyType.supplyTypeID} exists in view data:`, existsInViewData);
              if (!existsInViewData) {
                combinedSupplyTypes.push({
                  ...supplyType,
                  activated: false, // Mark as not activated since it's new
                  fromViewData: false // Flag to identify it's new from API
                });
              }
            });
            
            //console.log('PlantConfigurationSection - Supply Types - combinedSupplyTypes:', combinedSupplyTypes);
            setSupplyTypes(combinedSupplyTypes);
          }

          // Fetch Drug Types - filter based on mode
          const drugTypesRes = await axios.get(
            `${API_BASE_URL}/fetchPlantDrugTypesDirect/true`
          );
          const allDrugTypes = drugTypesRes.data?.data?.drugTypeList || [];
          
          //console.log('PlantConfigurationSection - readOnly:', readOnly);
          //console.log('PlantConfigurationSection - formData:', formData);
          //console.log('PlantConfigurationSection - formData.drugTypeIDList:', formData?.drugTypeIDList);
          //console.log('PlantConfigurationSection - allDrugTypes:', allDrugTypes);
          
          if (readOnly) {
            // In read-only mode, only show drug types that exist in viewCmoDetails data
            if (formData?.drugTypeIDList?.length > 0) {
              const filteredDrugTypes = allDrugTypes.filter(drugType => 
                formData.drugTypeIDList.some(item => {
                  const id = typeof item === 'object' ? item.drugTypeID : item;
                  return Number(id) === Number(drugType.drugTypeID);
                })
              );
              setDrugTypes(filteredDrugTypes);
            } else {
              // No items selected, show empty array
              setDrugTypes([]);
            }
          } else if (!readOnly) {
            // In edit mode (!readOnly), combine viewCmoDetails data with new drug types from API
            //console.log('PlantConfigurationSection - ENTERING EDIT MODE LOGIC');
            const combinedDrugTypes = [];
            
            // First, add drug types from viewCmoDetails data (with their current state)
            if (formData?.drugTypeIDList && formData.drugTypeIDList.length > 0) {
              //console.log('PlantConfigurationSection - Processing viewCmoDetails drug types:', formData.drugTypeIDList);
              formData.drugTypeIDList.forEach(item => {
                const drugTypeId = typeof item === 'object' ? item.drugTypeID : item;
                //console.log('PlantConfigurationSection - Looking for drugTypeId:', drugTypeId);
                const existingDrugType = allDrugTypes.find(dt => Number(dt.drugTypeID) === Number(drugTypeId));
                //console.log('PlantConfigurationSection - Found existing drug type:', existingDrugType);
                if (existingDrugType) {
                  combinedDrugTypes.push({
                    ...existingDrugType,
                    activated: true, // Mark as activated since it exists in viewCmoDetails
                    fromViewData: true // Flag to identify it came from viewCmoDetails
                  });
                }
              });
            }
            
            // Then, add new drug types from API that don't exist in viewCmoDetails
            //console.log('PlantConfigurationSection - Processing new drug types from API');
            allDrugTypes.forEach(drugType => {
              const existsInViewData = formData?.drugTypeIDList?.some(item => {
                const id = typeof item === 'object' ? item.drugTypeID : item;
                return Number(id) === Number(drugType.drugTypeID);
              });
              //console.log(`PlantConfigurationSection - Drug type ${drugType.drugTypeID} exists in view data:`, existsInViewData);
              if (!existsInViewData) {
                combinedDrugTypes.push({
                  ...drugType,
                  activated: false, // Mark as not activated since it's new
                  fromViewData: false // Flag to identify it's new from API
                });
              }
            });
            
            //console.log('PlantConfigurationSection - combinedDrugTypes:', combinedDrugTypes);
            setDrugTypes(combinedDrugTypes);
          }

          // Fetch Plant Types
          const plantTypesRes = await axios.get(
            `${API_BASE_URL}/fetchPlantTypesDirect/true`
          );
          const allPlantTypes = plantTypesRes.data?.data?.plantTypeList || [];
          
          //console.log('PlantConfigurationSection - Plant Types - readOnly:', readOnly);
          //console.log('PlantConfigurationSection - Plant Types - formData.plantTypeIDList:', formData?.plantTypeIDList);
          //console.log('PlantConfigurationSection - Plant Types - allPlantTypes:', allPlantTypes);
          
          if (readOnly) {
            // In read-only mode, only show plant types that exist in viewCmoDetails data
            if (formData?.plantTypeIDList?.length > 0) {
              const filteredPlantTypes = allPlantTypes.filter(plantType => 
                formData.plantTypeIDList.some(item => {
                  const id = typeof item === 'object' ? item.plantTypeID : item;
                  return Number(id) === Number(plantType.plantTypeID);
                })
              );
              setPlantTypes(filteredPlantTypes);
            } else {
              // No items selected, show empty array
              setPlantTypes([]);
            }
          } else if (!readOnly) {
            // In edit mode (!readOnly), combine viewCmoDetails data with new plant types from API
            //console.log('PlantConfigurationSection - Plant Types - ENTERING EDIT MODE LOGIC');
            const combinedPlantTypes = [];
            
            // First, add plant types from viewCmoDetails data (with their current state)
            if (formData?.plantTypeIDList && formData.plantTypeIDList.length > 0) {
              //console.log('PlantConfigurationSection - Plant Types - Processing viewCmoDetails plant types:', formData.plantTypeIDList);
              formData.plantTypeIDList.forEach(item => {
                const plantTypeId = typeof item === 'object' ? item.plantTypeID : item;
                //console.log('PlantConfigurationSection - Plant Types - Looking for plantTypeId:', plantTypeId);
                const existingPlantType = allPlantTypes.find(pt => Number(pt.plantTypeID) === Number(plantTypeId));
                //console.log('PlantConfigurationSection - Plant Types - Found existing plant type:', existingPlantType);
                if (existingPlantType) {
                  combinedPlantTypes.push({
                    ...existingPlantType,
                    activated: true, // Mark as activated since it exists in viewCmoDetails
                    fromViewData: true // Flag to identify it came from viewCmoDetails
                  });
                }
              });
            }
            
            // Then, add new plant types from API that don't exist in viewCmoDetails
            //console.log('PlantConfigurationSection - Plant Types - Processing new plant types from API');
            allPlantTypes.forEach(plantType => {
              const existsInViewData = formData?.plantTypeIDList?.some(item => {
                const id = typeof item === 'object' ? item.plantTypeID : item;
                return Number(id) === Number(plantType.plantTypeID);
              });
              //console.log(`PlantConfigurationSection - Plant Types - Plant type ${plantType.plantTypeID} exists in view data:`, existsInViewData);
              if (!existsInViewData) {
                combinedPlantTypes.push({
                  ...plantType,
                  activated: false, // Mark as not activated since it's new
                  fromViewData: false // Flag to identify it's new from API
                });
              }
            });
            
            //console.log('PlantConfigurationSection - Plant Types - combinedPlantTypes:', combinedPlantTypes);
            setPlantTypes(combinedPlantTypes);
          }

          // Mark data as loaded only if we have at least one successful fetch
          if (
            facilityTypesRes.data?.data?.facilityTypesList?.length > 0 ||
            supplyTypesRes.data?.data?.supplyTypeList?.length > 0 ||
            drugTypesRes.data?.data?.drugTypeList?.length > 0 ||
            plantTypesRes.data?.data?.plantTypeList?.length > 0
          ) {
            dataLoadedRef.current = true;
          } else {
            console.warn("No data returned from any API endpoint");
            // Allow retrying if no data was found
            dataLoadedRef.current = false;
          }
        } catch (error) {
          console.error("Error fetching configuration data:", error);
          setError("Failed to fetch configuration data. Please try again.");
          // Allow retrying on error
          dataLoadedRef.current = false;
        } finally {
          setTabsLoading(false);
        }
      };

      fetchConfigData();
    }, [readOnly, vendorMosymphonyID, brandOwnerMosymphonyID, API_BASE_URL]);

    // Initialize selected configs when data is loaded or formData changes
    useEffect(() => {
      // Only initialize selections when we have data from API
      if (
        drugTypes?.length > 0 ||
        supplyTypes?.length > 0 ||
        plantTypes?.length > 0 ||
        facilityTypes?.length > 0
      ) {
        try {
          let productModalitySelections = [],
            manufacturingSupplyTypeSelections = [],
            plantTypeSelections = [],
            manufacturingFacilityTypeSelections = [];


          // Always use the formData ID lists to determine selections
          // Handle both object format {typeID, typeName} and simple ID format
          const drugTypeIDList = formData?.drugTypeIDList?.map((item) => {
            const id = typeof item === 'object' ? item.drugTypeID : item;
            return Number(id) || 0;
          }) || [];
          const supplyTypeIDList = formData?.supplyTypeIDList?.map((item) => {
            const id = typeof item === 'object' ? item.supplyTypeID : item;
            return Number(id) || 0;
          }) || [];
          const plantTypeIDList = formData?.plantTypeIDList?.map((item) => {
            const id = typeof item === 'object' ? item.plantTypeID : item;
            return Number(id) || 0;
          }) || [];
          const facilityTypeIDList = formData?.facilityTypeIDList?.map((item) => {
            const id = typeof item === 'object' ? item.facilityTypeID : item;
            return Number(id) || 0;
          }) || [];

          // Initialize based on formData IDs matched against API data
          if (drugTypes?.length > 0) {
            productModalitySelections = drugTypes.map((type) => {
              const typeId = type && type.drugTypeID ? Number(type.drugTypeID) : null;
              if (typeId === null) {
                console.warn("Drug type missing drugTypeID:", type);
                return false;
              }
              // In readonly mode, if this item is in the filtered list, it means it's selected
              // In edit mode, check against the ID list
              const isSelected = readOnly ? true : drugTypeIDList.includes(typeId);
              return isSelected;
            });
          }

          if (supplyTypes?.length > 0) {
            manufacturingSupplyTypeSelections = supplyTypes.map((type) => {
              const typeId = type && type.supplyTypeID ? Number(type.supplyTypeID) : null;
              if (typeId === null) {
                console.warn("Supply type missing supplyTypeID:", type);
                return false;
              }
              // In readonly mode, if this item is in the filtered list, it means it's selected
              // In edit mode, check against the ID list
              const isSelected = readOnly ? true : supplyTypeIDList.includes(typeId);

              return isSelected;
            });
          }

          if (plantTypes?.length > 0) {
            plantTypeSelections = plantTypes.map((type) => {
              const typeId = type && type.plantTypeID ? Number(type.plantTypeID) : null;
              if (typeId === null) {
                console.warn("Plant type missing plantTypeID:", type);
                return false;
              }
              // In readonly mode, if this item is in the filtered list, it means it's selected
              // In edit mode, check against the ID list
              const isSelected = readOnly ? true : plantTypeIDList.includes(typeId);

              return isSelected;
            });
          }

          if (facilityTypes?.length > 0) {
            manufacturingFacilityTypeSelections = facilityTypes.map((type) => {
              const typeId = type && type.facilityTypeID ? Number(type.facilityTypeID) : null;
              if (typeId === null) {
                console.warn("Facility type missing facilityTypeID:", type);
                return false;
              }
              // In readonly mode, if this item is in the filtered list, it means it's selected
              // In edit mode, check against the ID list
              const isSelected = readOnly ? true : facilityTypeIDList.includes(typeId);

              return isSelected;
            });
          }

          setSelectedConfigs({
            productModality: productModalitySelections || [],
            manufacturingSupplyType: manufacturingSupplyTypeSelections || [],
            plantType: plantTypeSelections || [],
            manufacturingFacilityType: manufacturingFacilityTypeSelections || [],
          });
        } catch (error) {
          console.error("Error initializing selections:", error);
          // Set empty arrays as fallback
          setSelectedConfigs({
            productModality: [],
            manufacturingSupplyType: [],
            plantType: [],
            manufacturingFacilityType: [],
          });
        }
      }
      // Run this effect when either the API data or formData changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      drugTypes?.length,
      supplyTypes?.length,
      plantTypes?.length,
      facilityTypes?.length,
      formData?.drugTypeIDList,
      formData?.supplyTypeIDList,
      formData?.plantTypeIDList,
      formData?.facilityTypeIDList,
      readOnly, // Recalculate when switching between edit and readonly modes
    ]);

    // Handle tab change
    const handleTabChange = (e, newValue) => {
      setTabIndex(newValue);
    };

    // Handle toggle switch changes
    const handleToggle = (tabKey, idx) => {
      // Update the local state
      setSelectedConfigs((prev) => {
        // Create a new array with the toggled value
        const newConfig = {
          ...prev,
          [tabKey]: prev[tabKey].map((val, i) => (i === idx ? !val : val)),
        };


        // Create arrays of selected IDs
        let updatedData = {};
        let idList = [];

        // Handle each tab type
        switch (tabKey) {
          case "productModality":
            if (drugTypes.length > 0) {
              idList = drugTypes
                .filter((_, i) => newConfig.productModality[i])
                .map((type) => type.drugTypeID);

              updatedData = { drugTypeIDList: idList };

              if (typeof onFieldChange === "function") {
                onFieldChange("drugTypeIDList", idList);
              }
            }
            break;

          case "manufacturingSupplyType":
            if (supplyTypes.length > 0) {
              idList = supplyTypes
                .filter((_, i) => newConfig.manufacturingSupplyType[i])
                .map((type) => type.supplyTypeID);

              updatedData = { supplyTypeIDList: idList };

              if (typeof onFieldChange === "function") {
                onFieldChange("supplyTypeIDList", idList);
              }
            }
            break;

          case "plantType":
            if (plantTypes.length > 0) {
              idList = plantTypes
                .filter((_, i) => newConfig.plantType[i])
                .map((type) => type.plantTypeID);

              updatedData = { plantTypeIDList: idList };

              if (typeof onFieldChange === "function") {
                onFieldChange("plantTypeIDList", idList);
              }
            }
            break;

          case "manufacturingFacilityType":
            if (facilityTypes.length > 0) {
              idList = facilityTypes
                .filter((_, i) => newConfig.manufacturingFacilityType[i])
                .map((type) => type.facilityTypeID);

              updatedData = { facilityTypeIDList: idList };

              if (typeof onFieldChange === "function") {
                onFieldChange("facilityTypeIDList", idList);
              }
            }
            break;
        }


        return newConfig;
      });
    };

    // Helper to map ID lists to boolean arrays for readOnly mode
    const getReadOnlySelections = (types, idList, idKey) => {
      if (!types || !Array.isArray(types) || types.length === 0) return [];
      if (!idList || !Array.isArray(idList) || idList.length === 0) return types.map(() => false);

      try {
        // Convert all IDs to numbers for consistent comparison
        const numericIdList = idList.map((id) => Number(id) || 0);

        // Log what's being selected for debugging
        const selections = types.map((type) => {
          const typeId = type && type[idKey] ? Number(type[idKey]) : null;
          if (typeId === null) {
            console.warn(`Missing ${idKey} for type:`, type);
            return false;
          }
          const isSelected = numericIdList.includes(typeId);

          return isSelected;
        });

        return selections;
      } catch (error) {
        console.error("Error in getReadOnlySelections:", error);
        return types.map(() => false);
      }
    };

    // Render tab content based on current tab index
    const renderTabContent = () => {
      if (tabsLoading) {
        return (
          <div className="loader-container">
            <CircularProgress color="primary" />
          </div>
        );
      }

      // Make sure we have valid data to work with - extract IDs from objects if needed
      const drugTypeIDList = (formData?.drugTypeIDList || []).map(item => {
        const id = typeof item === 'object' ? item.drugTypeID : item;
        return Number(id) || 0;
      });
      const supplyTypeIDList = (formData?.supplyTypeIDList || []).map(item => {
        const id = typeof item === 'object' ? item.supplyTypeID : item;
        return Number(id) || 0;
      });
      const plantTypeIDList = (formData?.plantTypeIDList || []).map(item => {
        const id = typeof item === 'object' ? item.plantTypeID : item;
        return Number(id) || 0;
      });
      const facilityTypeIDList = (formData?.facilityTypeIDList || []).map(item => {
        const id = typeof item === 'object' ? item.facilityTypeID : item;
        return Number(id) || 0;
      });

      // Ensure arrays are initialized properly
      const safeProductModalityConfigs = Array.isArray(selectedConfigs.productModality) ?
        selectedConfigs.productModality : [];
      const safeManufacturingSupplyTypeConfigs = Array.isArray(selectedConfigs.manufacturingSupplyType) ?
        selectedConfigs.manufacturingSupplyType : [];
      const safePlantTypeConfigs = Array.isArray(selectedConfigs.plantType) ?
        selectedConfigs.plantType : [];
      const safeManufacturingFacilityTypeConfigs = Array.isArray(selectedConfigs.manufacturingFacilityType) ?
        selectedConfigs.manufacturingFacilityType : [];

      switch (tabIndex) {
        case 0:
          return (
            <ProductModalityTab
              drugTypes={drugTypes || []}
              selectedConfigs={
                readOnly
                  ? getReadOnlySelections(
                    drugTypes || [],
                    drugTypeIDList,
                    "drugTypeID"
                  )
                  : safeProductModalityConfigs
              }
              onToggle={(idx) => handleToggle("productModality", idx)}
              readOnly={readOnly}
              isLoading={tabsLoading}
            />
          );
        case 1:
          return (
            <ManufacturingSupplyTypeTab
              supplyTypes={supplyTypes || []}
              selectedConfigs={
                readOnly
                  ? getReadOnlySelections(
                    supplyTypes || [],
                    supplyTypeIDList,
                    "supplyTypeID"
                  )
                  : safeManufacturingSupplyTypeConfigs
              }
              onToggle={(idx) => handleToggle("manufacturingSupplyType", idx)}
              readOnly={readOnly}
              isLoading={tabsLoading}
            />
          );
        case 2:
          return (
            <PlantTypeTab
              plantTypes={plantTypes || []}
              selectedConfigs={
                readOnly
                  ? getReadOnlySelections(
                    plantTypes || [],
                    plantTypeIDList,
                    "plantTypeID"
                  )
                  : safePlantTypeConfigs
              }
              onToggle={(idx) => handleToggle("plantType", idx)}
              readOnly={readOnly}
              isLoading={tabsLoading}
            />
          );
        case 3:
          return (
            <ManufacturingFacilityTypeTab
              facilityTypes={facilityTypes || []}
              selectedConfigs={
                readOnly
                  ? getReadOnlySelections(
                    facilityTypes || [],
                    facilityTypeIDList,
                    "facilityTypeID"
                  )
                  : safeManufacturingFacilityTypeConfigs
              }
              onToggle={(idx) => handleToggle("manufacturingFacilityType", idx)}
              readOnly={readOnly}
              isLoading={tabsLoading}
            />
          );
        default:
          return null;
      }
    };

    return (
      <div className="cmo-stepper-wrapper plant-details-step">
        <div className="stepper-container">
          <form>

            {error && (
              <Typography color="error" style={{ marginBottom: 16 }}>
                {error}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    // Reset flags to allow refetching
                    dataLoadedRef.current = false;
                    apiCallAttemptedRef.current = false;
                    // Clear error
                    setError("");
                    // Force re-render which will trigger the data loading effect
                    setTabsLoading(true);
                  }}
                  style={{
                    marginLeft: 16,
                    background: '#1890ff',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              </Typography>
            )}

            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 16 }}
            >
              <Tab label={`Product Modality (${drugTypes?.length || 0})`} />
              <Tab label={`Manufacturing Supply Type (${supplyTypes?.length || 0})`} />
              <Tab label={`Plant Type (${plantTypes?.length || 0})`} />
              <Tab
                label={`Manufacturing Facility Type (${facilityTypes?.length || 0})`}
              />
            </Tabs>



            <div className="tab-content-container">{renderTabContent()}</div>


          </form>
          <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />

        </div>
      </div>
    );
  }
);

export default PlantConfigurationSection;
